
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ChefHat, Clock, Utensils, RefreshCw, Check, XCircle, BookOpenCheck, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import PageTransition from "@/components/PageTransition";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface Recipe {
  id: string;
  title: string;
  description: string;
  ingredients: string[];
  instructions: string;
  expiring_items: string[];
  is_prepared: boolean;
  created_at: string;
}

const RecipeSuggestions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [generatingRecipes, setGeneratingRecipes] = useState(false);
  const [expiringItemsCount, setExpiringItemsCount] = useState(0);
  const [userData, setUserData] = useState<{persona: string} | null>(null);
  
  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }
    
    // Check if user has household persona
    const checkUserPersona = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('persona')
          .eq('id', user.id)
          .single();
          
        if (error) throw error;
        
        setUserData(data);
        
        // Redirect if not household persona
        if (data?.persona !== 'household') {
          toast.error("This feature is only available for Household users");
          navigate("/dashboard");
        }
      } catch (error) {
        console.error("Error checking user persona:", error);
      }
    };
    
    checkUserPersona();
    fetchRecipes();
    checkExpiringItems();
  }, [user, navigate]);
  
  const fetchRecipes = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('recipe_suggestions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      setRecipes(data || []);
    } catch (error) {
      console.error("Error fetching recipes:", error);
      toast.error("Failed to load recipe suggestions");
    } finally {
      setLoading(false);
    }
  };
  
  const checkExpiringItems = async () => {
    if (!user) return;
    
    try {
      const now = new Date();
      const sevenDaysLater = new Date();
      sevenDaysLater.setDate(now.getDate() + 7);
      
      const { count, error } = await supabase
        .from('inventory_items')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('has_expiry', true)
        .gte('expiry_date', now.toISOString().split('T')[0])
        .lte('expiry_date', sevenDaysLater.toISOString().split('T')[0]);
        
      if (error) throw error;
      
      setExpiringItemsCount(count || 0);
    } catch (error) {
      console.error("Error checking expiring items:", error);
    }
  };
  
  const generateRecipes = async () => {
    if (!user) return;
    
    try {
      setGeneratingRecipes(true);
      
      const response = await supabase.functions.invoke('generate-recipe-suggestions', {
        body: JSON.stringify({ userId: user.id })
      });
      
      if (response.error) {
        throw new Error(response.error.message);
      }
      
      if (response.data.suggestions && response.data.suggestions.length > 0) {
        toast.success("New recipe suggestions generated!");
        fetchRecipes();
      } else {
        toast.info("No recipes could be generated with your current expiring items");
      }
    } catch (error) {
      console.error("Error generating recipes:", error);
      toast.error("Failed to generate recipes");
    } finally {
      setGeneratingRecipes(false);
    }
  };
  
  const markAsPrepared = async (recipeId: string, expiringItems: string[]) => {
    try {
      // Update recipe as prepared
      const { error: recipeError } = await supabase
        .from('recipe_suggestions')
        .update({ is_prepared: true })
        .eq('id', recipeId);
        
      if (recipeError) throw recipeError;
      
      // Mark expiring items as used (set expiry date to today)
      const today = new Date().toISOString().split('T')[0];
      
      for (const itemId of expiringItems) {
        await supabase
          .from('inventory_items')
          .update({ expiry_date: today })
          .eq('id', itemId);
      }
      
      toast.success("Recipe marked as prepared and items updated!");
      fetchRecipes();
      checkExpiringItems();
    } catch (error) {
      console.error("Error marking recipe as prepared:", error);
      toast.error("Failed to update recipe status");
    }
  };
  
  const deleteRecipe = async (recipeId: string) => {
    try {
      const { error } = await supabase
        .from('recipe_suggestions')
        .delete()
        .eq('id', recipeId);
        
      if (error) throw error;
      
      toast.success("Recipe suggestion removed");
      fetchRecipes();
    } catch (error) {
      console.error("Error deleting recipe:", error);
      toast.error("Failed to delete recipe");
    }
  };
  
  // Check for household persona
  if (userData && userData.persona !== 'household') {
    return (
      <PageTransition className="min-h-screen bg-gray-50 pt-24 pb-16">
        <div className="container mx-auto px-4">
          <Alert className="my-8">
            <XCircle className="h-4 w-4" />
            <AlertTitle>Access Restricted</AlertTitle>
            <AlertDescription>
              The recipe suggestions feature is exclusively available for Household users.
            </AlertDescription>
          </Alert>
        </div>
      </PageTransition>
    );
  }
  
  return (
    <PageTransition className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center">
              <ChefHat className="mr-2 h-6 w-6 text-expitrack-primary" />
              Recipe Suggestions
            </h1>
            <p className="text-gray-600">
              Smart recipes based on your expiring ingredients
            </p>
          </div>
          
          <div className="mt-4 md:mt-0">
            <Button 
              onClick={generateRecipes} 
              disabled={generatingRecipes || expiringItemsCount === 0}
              className="bg-expitrack-primary hover:bg-expitrack-primary/90"
            >
              {generatingRecipes ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Generate New Recipes
                </>
              )}
            </Button>
          </div>
        </div>
        
        {expiringItemsCount === 0 && (
          <Alert className="mb-6">
            <Clock className="h-4 w-4" />
            <AlertTitle>No expiring ingredients</AlertTitle>
            <AlertDescription>
              You don't have any ingredients expiring within the next 7 days.
              Add more items to your inventory or update existing ones to get recipe suggestions.
            </AlertDescription>
          </Alert>
        )}
        
        <Tabs defaultValue="active" className="mb-8">
          <TabsList>
            <TabsTrigger value="active">Active Suggestions</TabsTrigger>
            <TabsTrigger value="prepared">Prepared Recipes</TabsTrigger>
          </TabsList>
          
          <TabsContent value="active">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-expitrack-primary" />
              </div>
            ) : recipes.filter(r => !r.is_prepared).length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                <BookOpenCheck className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No recipe suggestions yet</h3>
                <p className="text-gray-500 max-w-md mx-auto mb-6">
                  Click on "Generate New Recipes" to get personalized suggestions based on your expiring ingredients.
                </p>
                <Button 
                  onClick={generateRecipes} 
                  disabled={generatingRecipes || expiringItemsCount === 0}
                  className="bg-expitrack-primary hover:bg-expitrack-primary/90"
                >
                  Generate Recipes
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {recipes
                  .filter(recipe => !recipe.is_prepared)
                  .map(recipe => (
                    <RecipeCard 
                      key={recipe.id} 
                      recipe={recipe} 
                      onPrepared={markAsPrepared}
                      onDelete={deleteRecipe}
                    />
                  ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="prepared">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-expitrack-primary" />
              </div>
            ) : recipes.filter(r => r.is_prepared).length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                <Utensils className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No prepared recipes yet</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  After you cook a recipe, mark it as prepared to keep track of what you've made.
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {recipes
                  .filter(recipe => recipe.is_prepared)
                  .map(recipe => (
                    <RecipeCard 
                      key={recipe.id} 
                      recipe={recipe} 
                      isPrepared 
                      onDelete={deleteRecipe}
                    />
                  ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </PageTransition>
  );
};

interface RecipeCardProps {
  recipe: Recipe;
  isPrepared?: boolean;
  onPrepared?: (id: string, expiringItems: string[]) => void;
  onDelete?: (id: string) => void;
}

const RecipeCard = ({ recipe, isPrepared = false, onPrepared, onDelete }: RecipeCardProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className={isPrepared ? "border-green-200 bg-green-50" : ""}>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-xl">{recipe.title}</CardTitle>
              <CardDescription className="mt-2">{recipe.description}</CardDescription>
            </div>
            {isPrepared && (
              <Badge className="bg-green-600">
                <Check className="mr-1 h-3 w-3" /> Prepared
              </Badge>
            )}
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="mb-4">
            <h4 className="font-medium mb-2">Ingredients:</h4>
            <ul className="list-disc pl-5 space-y-1">
              {recipe.ingredients.map((ingredient, idx) => (
                <li key={idx} className={ingredient.includes('*') ? "text-expitrack-primary font-medium" : ""}>
                  {ingredient.replace('*', '')}
                  {ingredient.includes('*') && <span className="ml-1 text-amber-600">(expiring)</span>}
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="font-medium mb-2">Instructions:</h4>
            <p className="text-gray-700 whitespace-pre-wrap">{recipe.instructions}</p>
          </div>
        </CardContent>
        
        <Separator />
        
        <CardFooter className="flex justify-between pt-4">
          {!isPrepared && onPrepared ? (
            <Button 
              variant="outline" 
              className="border-green-500 text-green-700 hover:bg-green-50"
              onClick={() => onPrepared(recipe.id, recipe.expiring_items)}
            >
              <Utensils className="mr-2 h-4 w-4" />
              Mark as Prepared
            </Button>
          ) : (
            <div className="text-xs text-gray-500">
              {new Date(recipe.created_at).toLocaleDateString()}
            </div>
          )}
          
          {onDelete && (
            <Button 
              variant="ghost" 
              className="text-gray-500 hover:text-red-600"
              onClick={() => onDelete(recipe.id)}
            >
              <XCircle className="h-4 w-4" />
            </Button>
          )}
        </CardFooter>
      </Card>
    </motion.div>
  );
};

export default RecipeSuggestions;
